import React, { useState } from "react";
import { Card, CardContent } from "../../../../shadcn/ui/card";
import { Badge } from "../../../../shadcn/ui/badge";
import { Button } from "../../../../shadcn/ui/button";
import {
  Users,
  MapPin,
  Wifi,
  Coffee,
  Monitor,
  Car,
  Video,
  Phone,
  Building2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const SpaceListView = ({ spaces, onSelectSpace }) => {
  const getAmenityIcon = amenity => {
    // Handle both string amenities (old format) and object amenities (new format)
    const amenityId = typeof amenity === "object" ? amenity.id : amenity.toLowerCase();

    const iconMap = {
      wifi: Wifi,
      coffee: Coffee,
      monitor: Monitor,
      parking: Car,
      video: Video,
      phone: Phone,
      power: Monitor, // Power outlets
      silence: Monitor, // Quiet zone
    };
    const IconComponent = iconMap[amenityId] || Monitor;
    return <IconComponent className="w-4 h-4" />;
  };

  const getAmenityName = amenity => {
    // Handle both string amenities (old format) and object amenities (new format)
    if (typeof amenity === "object") {
      return amenity.label || amenity.name;
    }
    return amenity.charAt(0).toUpperCase() + amenity.slice(1);
  };

  const getStatusBadge = status => {
    // Don't show badge for published status (it's the default state)
    if (!status || status.toLowerCase() === "published") {
      return null;
    }

    switch (status.toLowerCase()) {
      case "available":
        return <Badge className="bg-green-100 text-green-800">Available</Badge>;
      case "occupied":
        return <Badge className="bg-red-100 text-red-800">Occupied</Badge>;
      case "booked":
        return <Badge className="bg-orange-100 text-orange-800">Booked</Badge>;
      case "maintenance":
        return <Badge className="bg-gray-100 text-gray-800">Maintenance</Badge>;
      default:
        return null; // Don't show badge for unknown status
    }
  };

  const ImageCarousel = ({ space, className = "" }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    // Get all images from the space
    // Filter out null, undefined, and empty strings
    const allImages = space.media?.images || (space.image ? [space.image] : []);
    const images = allImages.filter(img => img && typeof img === "string" && img.trim() !== "");
    const hasMultipleImages = images.length > 1;

    const goToNext = e => {
      e.stopPropagation();
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    };

    const goToPrev = e => {
      e.stopPropagation();
      setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
    };

    const goToImage = (index, e) => {
      e.stopPropagation();
      setCurrentImageIndex(index);
    };

    return (
      <div
        className={`w-32 h-32 relative flex-shrink-0 ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {images.length > 0 ? (
          <>
            <img
              src={images[currentImageIndex]}
              alt={`${space.name} - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover rounded-lg transition-opacity duration-300"
              onError={e => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div
              className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-dashed border-blue-200 rounded-lg"
              style={{ display: "none" }}
            >
              <Building2 className="w-8 h-8 text-blue-400" />
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-dashed border-blue-200 rounded-lg">
            <Building2 className="w-8 h-8 text-blue-400" />
          </div>
        )}

        {/* Navigation Arrows - Show on hover if multiple images */}
        {hasMultipleImages && isHovered && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1 shadow-lg transition-all z-10"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-3 h-3 text-gray-800" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1 shadow-lg transition-all z-10"
              aria-label="Next image"
            >
              <ChevronRight className="w-3 h-3 text-gray-800" />
            </button>
          </>
        )}

        {/* Image Indicators - Show if multiple images */}
        {hasMultipleImages && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={e => goToImage(index, e)}
                className={`h-1 rounded-full transition-all ${
                  index === currentImageIndex ? "bg-white w-4" : "bg-white/60 hover:bg-white/80 w-1"
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {spaces.map(space => (
        <Card
          key={space.id}
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => onSelectSpace(space)}
        >
          <CardContent className="p-6">
            <div className="flex gap-6">
              {/* Space Image */}
              <ImageCarousel space={space} />

              {/* Space Info */}
              <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0 mr-2">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-1" title={space.name}>
                      {space.name}
                    </h3>
                    <div className="flex items-center mt-1 text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span className="truncate" title={space.location}>
                        {space.location}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {getStatusBadge(space.status)}
                    <Badge variant="outline" className="whitespace-nowrap">
                      {space.type}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {space.capacity} people
                  </div>
                  <div className="font-semibold text-lg text-gray-900 truncate">{space.pricing}</div>
                </div>

                {/* Amenities */}
                <div className="flex-1 mb-4">
                  {space.amenities && space.amenities.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {space.amenities.slice(0, 6).map((amenity, index) => (
                        <div key={index} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-xs">
                          {getAmenityIcon(amenity)}
                          <span className="truncate max-w-[120px]">{getAmenityName(amenity)}</span>
                        </div>
                      ))}
                      {space.amenities.length > 6 && (
                        <div className="flex items-center px-2 py-1 bg-gray-100 rounded-md text-xs text-gray-600">
                          +{space.amenities.length - 6} more
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions - Always at bottom */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="whitespace-nowrap text-xs"
                    onClick={e => {
                      e.stopPropagation();
                      onSelectSpace(space);
                    }}
                    disabled={space.status !== "available"}
                  >
                    {space.status === "available" ? "Book Now" : "View Schedule"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap text-xs"
                    onClick={e => {
                      e.stopPropagation();
                      onSelectSpace(space);
                    }}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default SpaceListView;
